import { v4 as uuidV4 } from 'uuid';
import { format } from 'date-fns';
import {
  aesEncrypt,
  bufferFrom,
  ByteStringBuffer,
  createCipher,
  decode64,
  encode64,
  generatePBKDF2Key,
  generateSymmetricKey,
  publicKeyFromPem,
  rsaEncrypt,
  splitSignedDataString,
} from '@pw-spk/general-app-web-cryptography';
import { isAxiosError } from 'axios';

import { FORM_EXCEPTIONS } from '../../configs/signallerForm.config';
import {
  getPublicKey,
  postEmail,
  postEncryptPdf,
  postSignallerReport,
} from '../../../../../../services/api/signaller/signaller.service';
import {
  APP_PARTNER_MAIL_TEMPLATE_ID,
  APP_PARTNER_NAME,
} from '../../../../../../configs/app.config';
import { APP_RECAPTCHA_SITE_KEY } from '../../../../../../configs/recaptcha.config';
import { putFileByUrl } from '../../../../../../services/aws/s3/s3.service';
import { saveAsFile } from '../../../../../../utils/file/file.util';
import { printText } from '../../../../../../utils/printer/printer.util';
import { calcFileSizeFromBytes } from '../../../../../../utils/file/size/fileSize.util';
import { encryptFile } from '../../../../../../utils/encryption/encryption.util';
import {
  getPartnerFullName,
  getPartnerName,
  isMultiPartner,
} from '../../../../../../utils/partner/partner.util';

import { PDF_CONTENT_TYPE } from './report.handler.type';
import { createPDFFile } from './pdf.handler';

import type {
  IEncryptPdfFileParams,
  IEncryptSymmetricKeyForClientReturn,
  IEncryptSymmetricKeyReturn,
  ISendEmailParams,
  TIsAnonimaParams,
} from './report.handler.type';
import type { TFunction } from '../../../../../../types/i18n/i18n.type';
import type { IPostEncryptPdfReturn } from '../../../../../../services/api/signaller/signaller.service.type';
import type {
  IHandleReportContext,
  ISignallerFormState,
} from '../../components/signallerForm/signallerForm.type';
import type { SymmetricKey } from '@pw-spk/general-app-web-cryptography';

const generateRandomString = (length: number, str = '') => {
  if (str.length === length) {
    return str;
  }

  const randomChar = String.fromCharCode(
    Math.floor(Math.random() * 62) + (Math.random() < 0.5 ? 48 : 65),
  );

  const validChar = /[A-Za-z0-9]/.test(randomChar)
    ? randomChar
    : String.fromCharCode(Math.floor(Math.random() * 10) + 48);

  return generateRandomString(length, str + validChar);
};

const encryptSymmetricKey = async (
  symmetricKey: SymmetricKey,
): Promise<IEncryptSymmetricKeyReturn> => {
  const { id: publicKeyId, publicKey: publicKeyPem } = await getPublicKey();

  const publicKey = publicKeyFromPem(publicKeyPem);

  const encryptedSymmetricKey = rsaEncrypt(
    publicKey,
    new Uint8Array(bufferFrom(symmetricKey.toHex(), 'hex')),
  );

  return {
    encryptedSymmetricKey,
    publicKeyId,
  };
};

const encryptSymmetricKeyForClient = async (
  symmetricKey: SymmetricKey,
): Promise<IEncryptSymmetricKeyForClientReturn> => {
  const password = generateRandomString(15);
  const { key: PBKDF2Key, salt } = generatePBKDF2Key(password);
  const cipher = createCipher(PBKDF2Key);

  const { encryptedData } = aesEncrypt(
    cipher,
    new Uint8Array(bufferFrom(symmetricKey.toHex(), 'hex')),
    salt,
  );

  return {
    encryptedSymmetricKey: encode64(new ByteStringBuffer(encryptedData).getBytes()),
    password,
    salt,
  };
};

const sendEmail = async ({
  attachment,
  hash,
  encryptedHash,
  reportNumber,
  email,
  client,
  clientFullName,
}: ISendEmailParams): Promise<void> => {
  if (email !== '') {
    await postEmail({
      templateId: APP_PARTNER_MAIL_TEMPLATE_ID,
      attachment: attachment
        ? {
            ...attachment,
            type: PDF_CONTENT_TYPE,
          }
        : undefined,
      hash: decode64(splitSignedDataString(hash).data),
      encryptedHash: decode64(splitSignedDataString(encryptedHash).data),
      referenceNumber: reportNumber,
      email,
      client,
      clientFullName,
    });
  }
};

const encryptPdfFile = async ({
  password,
  confirmPassword,
  file,
}: IEncryptPdfFileParams): Promise<IPostEncryptPdfReturn | undefined> => {
  if (!password || !confirmPassword) {
    return undefined;
  }

  return postEncryptPdf({
    password,
    confirmPassword,
    file: encode64(new ByteStringBuffer(file).getBytes()),
  });
};

export const isAnonim = ({ reporter, address, email, phone }: TIsAnonimaParams): boolean =>
  reporter === '' && address === '' && email === '' && phone[1] === '';

/**
 * Send report
 *
 * Required load recaptcha API in useExternalScript(APP_RECAPTCHA_API_URL);
 */
export const handleReport = async (
  {
    email,
    personalDataDisclosure,
    evidenceFiles,
    password,
    confirmPassword,
    subject,
    partnerNames,
    recaptchaToken,
    street,
    zip,
    city,
    country,
    ...data
  }: ISignallerFormState & { recaptchaToken: string },
  { setModal, t, handleReset, handleSetError, availableSubjects }: IHandleReportContext,
): Promise<void> => {
  try {
    const creationDate = format(new Date(), 'yyyy-MM-dd HH:mm');
    const generatedUUID = uuidV4().replace(/-/g, '');
    const reportNumber = generatedUUID.substring(generatedUUID.length - 6).toUpperCase();

    const address = [street, zip, city, country]
      .filter((field) => field && field.trim() !== '')
      .join(', ');

    const personalDisclosure = personalDataDisclosure[0] === 'true';
    const pdfFile = await createPDFFile({
      ...data,
      email,
      reportNumber,
      creationDate,
      subject,
      personalDataDisclosure: personalDisclosure,
      t,
      address,
      evidenceFiles: evidenceFiles.map(
        ({ name, size }) => `${name} (${calcFileSizeFromBytes(size)})`,
      ),
      availableSubjects,
    });

    const anonim = isAnonim({ email, address, ...data });
    const fileSubject = subject.join('-');

    const reportFileName = `${creationDate.replaceAll(' ', '_').replaceAll(':', '-')}_${
      isMultiPartner()
        ? partnerNames[0].split('-').pop() || ''.replaceAll(' ', '_')
        : APP_PARTNER_NAME
    }${anonim ? '_ANONIM' : ''}_${fileSubject}_${reportNumber}`.toUpperCase();
    const reportFileNamePDF = `${reportFileName}.pdf`;

    const symmetricKey = await generateSymmetricKey();
    const [
      { encryptedSymmetricKey, publicKeyId },
      {
        encryptedSymmetricKey: encryptedSignallerKey,
        password: encryptedSignallerKeyPassword,
        salt: encryptedSignallerKeySalt,
      },
    ] = await Promise.all([
      encryptSymmetricKey(symmetricKey),
      encryptSymmetricKeyForClient(symmetricKey),
    ]);

    const cipher = createCipher(symmetricKey);

    const encryptedFilesData = await Promise.all(
      [new File([pdfFile], reportFileNamePDF, { type: PDF_CONTENT_TYPE })]
        .concat(...evidenceFiles)
        .map(async (file) => encryptFile(cipher, file)),
    );

    const uploadedFilesData = await postSignallerReport({
      // First file always is report PDF
      files: encryptedFilesData.map(({ encryptedData, ...filesData }) => ({
        ...filesData,
        durableMedium: false,
      })),
      documentConfiguration: {
        encryptedKey: encryptedSymmetricKey,
        encryptedSignallerKey: {
          encryptedKey: encryptedSignallerKey,
          salt: encryptedSignallerKeySalt,
        },
        keyId: publicKeyId,
        documentName: reportFileName,
        subject: fileSubject,
        documentTypes: subject,
      },
      signallerConfiguration: {
        referenceNumber: reportNumber,
        client: getPartnerName(),
        clientFullName: getPartnerFullName(),
        password,
        confirmPassword,
        email,
        consents: personalDisclosure ? ['disclosure'] : [],
      },
      recaptchaToken,
    });

    // Upload do AWS S3
    if (encryptedFilesData[0]) {
      await Promise.all(
        encryptedFilesData.map(async ({ encryptedData, name }) => {
          const uploadedFile = uploadedFilesData.find(
            ({ name: uploadedName }) => name === uploadedName,
          );
          if (!uploadedFile) {
            throw new Error('Can`t upload file to S3');
          }
          const { url, headers } = uploadedFile;
          // Upload do AWS S3
          await putFileByUrl(url, headers, encryptedData.buffer);
        }),
      );
      const encryptedPdfFile = await encryptPdfFile({
        password,
        confirmPassword,
        file: new Uint8Array(await pdfFile.arrayBuffer()),
      });
      await sendEmail({
        attachment: encryptedPdfFile
          ? {
              fileName: reportFileNamePDF,
              content: encryptedPdfFile.file,
            }
          : undefined,
        hash: encryptedFilesData[0].hash,
        encryptedHash: encryptedFilesData[0].encryptedHash,
        reportNumber,
        email,
        client: getPartnerName(),
        clientFullName: getPartnerFullName(),
      });
      const encryptedPdfFileBlob = encryptedPdfFile
        ? new Blob([new Uint8Array(bufferFrom(encryptedPdfFile.file, 'base64'))], {
            type: PDF_CONTENT_TYPE,
          })
        : undefined;
      const printHeader = t('headers.keyPasswordFile');
      const keyPasswordLabel = t('labels.keyPassword');
      setModal({
        type: 'success',
        handleDownload: () => {
          saveAsFile(encryptedPdfFileBlob ?? pdfFile, reportFileNamePDF);
        },
        hash: decode64(splitSignedDataString(encryptedFilesData[0].hash).data),
        encryptedSignallerKeyPassword,
        isPasswordFilled: password !== '',
        password,
        reportNumber,
        handlePrintPassphrase: () => {
          printText(`<h3>${printHeader}</h3>${keyPasswordLabel}: ${encryptedSignallerKeyPassword}`);
        },
        handleSavePassphrase: () => {
          saveAsFile(
            new Blob([`${printHeader}\n${keyPasswordLabel}: ${encryptedSignallerKeyPassword}`]),
            `${reportFileName}.txt`,
          );
        },
      });
      handleReset();
    }
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error('report.handler:249', { error });

    if (isAxiosError(error) && error.response?.status === 422) {
      handleSetError(t('invalid.recaptcha'));
    } else {
      setModal({
        type: 'error',
      });
    }
  }
};

export const recaptchaValidation = async (
  t: TFunction,
  handleSetError: (msg: string) => void,
): Promise<string | undefined> => {
  try {
    const { grecaptcha } = window;

    if (!grecaptcha) {
      throw new Error(FORM_EXCEPTIONS.noRecaptcha);
    }

    return await grecaptcha.execute(APP_RECAPTCHA_SITE_KEY, { action: 'submit' });
  } catch (error: unknown) {
    // eslint-disable-next-line no-console
    console.error('report.recaptchaValidation:249', { error });

    handleSetError(t('invalid.recaptcha'));

    return undefined;
  }
};
